/**
 * @file tour-orchestrator.service.ts
 * @description Servicio orquestador del sistema de Onboarding (Tours Guiados).
 *
 * DATA-DRIVEN: los pasos del tour ya NO viven en mocks estáticos. Se resuelven en runtime desde el estado
 * reactivo global (`OnboardingService.state$`) buscando la tarea por `task_id` y adaptando su
 * `guided_tour.steps` (contrato del backend, snake_case) al contrato `TourStep` que consume el blackout.
 *
 * Responsabilidades:
 *  1. Estado reactivo del tour activo (BehaviorSubject<TourState>).
 *  2. Kill-switch tolerante en NavigationEnd (solo cierra si cambia el módulo raíz).
 *  3. Control del tour (startTourByTaskId, requestTourForTask, nextStep, endTour).
 *  4. Persistencia/restauración del progreso ante refresh/re-login (sessionStorage, reconstruido desde state$).
 *  5. Inyección de hooks de layout móvil por `target_selector` (el JSON del backend no puede traer funciones).
 */

import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { InstructionsModalService } from '@common/services/instructions-modal.service';
import { BehaviorSubject, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, delay, filter, map, take, takeUntil, timeout } from 'rxjs/operators';

import { OnboardingState, OnboardingTask } from '../interfaces/onboarding.interfaces';
import { GuidedTour, TourState, TourStep, TourStepPlacement } from '../interfaces/tour.interfaces';
import { TASK_VIDEO_KEY_MAP } from './onboarding.config';
import { OnboardingService } from './onboarding.service';
import { BlackoutTrigger, OnboardingAnalyticsService } from './onboarding-analytics.service';

// Reverse-lookup `video_key` → `task_id` (el `showModal` entrega la key del video; necesitamos su tarea).
// Se computa una sola vez a partir del mapa único `TASK_VIDEO_KEY_MAP` (Cero Cables: la relación vive en config).
const VIDEO_KEY_TO_TASK: Record<string, string> = Object.entries(TASK_VIDEO_KEY_MAP).reduce((acc, [taskId, videoKey]) => ((acc[videoKey] = taskId), acc), {} as Record<string, string>);

// ─── Constantes de configuración ─────────────────────────────────────────────

// ─── Gate "Aduana Universal": no lanzar el Blackout mientras la pantalla esté ocupada por un modal ──────
//
// DOM-agnóstico (ignora QUIÉN abre el modal; solo mira si la UI está visualmente bloqueada). Gate de UNA
// fase (ver `startTourByTaskId`): desde Fase LL el video del módulo se DIFIERE, así que ya no hay que esperar
// a que "aparezca" un modal — solo evitamos pintar encima de un modal INCIDENTAL que YA esté abierto
// (plan-permission, SweetAlert, otro ngb): si la UI está libre → arrancar YA; si hay modal → esperar a que
// se despeje + un settle corto.

/** Intervalo de sondeo del estado de la UI (ms). */
const TOUR_GATE_POLL_MS = 300;

/**
 * Settle corto (ms) tras quedar la pantalla DESPEJADA (p. ej. al cerrar el modal) antes de mostrar el
 * Blackout. Mantiene el arranque ágil tras cerrar (ej. en Cuentas) y evita pintar en mitad de la animación.
 */
const TOUR_GATE_CLEAR_SETTLE_MS = 300;

/**
 * Retardo (ms) del "hand-off" entre módulos (ej. redirect POS→Inventario por inventario vacío) antes de armar
 * el gate. Supera el fade-out del modal saliente de ngb (~150 ms): si armáramos el gate de inmediato,
 * `_isUiBlocked()` vería el backdrop del modal viejo aún en el DOM y el race se engancharía a SU desaparición,
 * arrancando antes de que abra el modal de la ruta nueva. Imperceptible (el tour igual espera al modal nuevo).
 */
const TOUR_HANDOFF_DEFER_MS = 200;

/**
 * Tope (ms) que la decisión de auto-show del video espera a que la carga del onboarding resuelva (`settled$`).
 * En el flujo normal `settled$` emite apenas el HTTP vuelve (200/404/5xx), muy por debajo de esto; este timeout
 * es solo la red de seguridad para el caso patológico en que `loadInitialState` no llegara a ejecutarse: al
 * vencer, la decisión resuelve `false` (NO suprimir) para no bloquear el video eternamente (CN-03).
 */
const ENTRY_VIDEO_DECISION_TIMEOUT_MS = 6000;

/**
 * Tope (ms) de la INTENCIÓN de arranque desde el sidebar cuando el clic llega ANTES de que cargue el estado
 * del onboarding (FINA-3295 B): la intención se recuerda y se re-resuelve al emitir `settled$`. QA midió que
 * con +6 s de latencia por petición el estado tarda ~8 s en llegar; 30 s lo cubre con holgura sin dejar la
 * suscripción viva para siempre si la carga nunca resolviera. NO se reutiliza el tope del video (6 s): ese
 * está afinado para no retrasar un modal, y aquí quedaría corto (descartaría la intención a los 6 s cuando
 * el estado llega a los 8 s).
 */
const NAV_LINK_INTENT_TIMEOUT_MS = 30000;

/**
 * Selector de modal/overlay VISIBLE. Solo clases que existen cuando algo está abierto encima de la UI:
 *  - `ngb-modal-window`      → ng-bootstrap (instructions modal, modal de Pedidos, etc.).
 *  - `.modal-backdrop`       → backdrop de ngb/bootstrap (presente solo mientras hay modal).
 *  - `.modal.show`           → modal nativo de Bootstrap visible.
 *  - `.swal2-container`      → SweetAlert2 (presente solo mientras hay alerta).
 *  - `.cdk-overlay-backdrop` → overlay de Angular CDK con backdrop (dialogs/modales), no tooltips/menús.
 * NO se usa `.modal` pelado ni `.cdk-overlay-container`: existen ocultos/permanentes en el DOM (templates
 * bancrecer, marketing, confirm-deletion, modal-block; el container del CDK) → falso positivo que trancaría
 * el tour para siempre. El Blackout (`.tour-overlay`) y el modal de celebración (`.onboarding-modal-overlay`)
 * no se auto-matchean.
 */
const BLOCKING_MODAL_SELECTOR = 'ngb-modal-window, .modal-backdrop, .modal.show, .swal2-container, .cdk-overlay-backdrop';

/** Claves de sessionStorage: sobreviven a F5 en la misma pestaña, se limpian al cerrarla. */
const TOUR_STORAGE_KEY = 'fina_onboarding_current_tour'; // guarda el task_id activo
const TOUR_STEP_KEY = 'fina_onboarding_current_step';

const INITIAL_STATE: TourState = {
    isActive: false,
    currentTour: null,
    currentStepIndex: 0,
};

// ─── Lifecycle hooks de layout para móvil ────────────────────────────────────
//
// Closures DOM-only (sin inyección de servicios → sin dependencias circulares, principio "Cero Cables")
// que preparan el layout en móvil ANTES de que el pipeline mida el targetElement.
//
// Selectores DOM: '.sidenav-toggled' (sidebar visible en móvil), '#sidebarToggle' (hamburguesa),
// '#check' (checkbox del panel del carrito), 'label.btn-right-panel' (abre/cierra el carrito).
// Breakpoint móvil: < 1200px.

const closeSidebarIfOpenMobile = (): Promise<void> => {
    if (window.innerWidth >= 1200) return Promise.resolve();
    if (document.querySelector('.sidenav-toggled')) {
        (document.getElementById('sidebarToggle') as HTMLElement | null)?.click();
        return new Promise(resolve => setTimeout(resolve, 350));
    }
    return Promise.resolve();
};

const openCartPanelIfClosedMobile = (): Promise<void> => {
    if (window.innerWidth >= 1200) return Promise.resolve();
    const checkEl = document.getElementById('check') as HTMLInputElement | null;
    if (checkEl && !checkEl.checked) {
        (document.querySelector('label.btn-right-panel') as HTMLElement | null)?.click();
        return new Promise(resolve => setTimeout(resolve, 350));
    }
    return Promise.resolve();
};

const closeCartPanelIfOpenMobile = (): Promise<void> => {
    if (window.innerWidth >= 1200) return Promise.resolve();
    const checkEl = document.getElementById('check') as HTMLInputElement | null;
    if (checkEl && checkEl.checked) {
        (document.querySelector('label.btn-right-panel') as HTMLElement | null)?.click();
        return new Promise(resolve => setTimeout(resolve, 350));
    }
    return Promise.resolve();
};

/**
 * Fábrica de hook `beforeShowAction` para un ancla del menú lateral (ítems anidados en "Configuración", ej.
 * "Cargos extra"/"Usuarios asociados"). Deja el ítem VISIBLE antes de que el Blackout mida su posición: si el
 * acordeón que lo contiene está colapsado o el ítem quedó fuera de vista (scroll), el `getBoundingClientRect`
 * del `<a>` cae cerca del fondo y el spotlight ancla mal (sobre "Invita y gana"/perfil). Pasos: (1) si el
 * acordeón está colapsado, clic en su `<a>` toggle (lo mismo que haría el usuario) para expandirlo;
 * (2) `scrollIntoView` para centrar el ítem en el contenedor scrolleable del sidebar. Se genera POR selector
 * para reusarse en cualquier paso que apunte a un ítem del sidebar. Corre en desktop y móvil; idempotente.
 */
const makeRevealSidebarItem = (selector: string) => (): Promise<void> => {
    const target = document.querySelector(selector);
    if (!target) return Promise.resolve();

    const nested = target.closest('nav.sidenav-menu-nested');
    const wasCollapsed = !!nested && nested.classList.contains('collapsed');
    if (wasCollapsed) {
        const toggleLink = nested!.previousElementSibling as HTMLElement | null;
        if (toggleLink && toggleLink.tagName === 'A') toggleLink.click(); // toggle() del ítem padre → expand()
    }

    return new Promise<void>(resolve => {
        // Esperar la animación de expansión (si aplicó) antes de scrollear/medir.
        setTimeout(
            () => {
                target.scrollIntoView({ block: 'center', inline: 'nearest' });
                setTimeout(resolve, 150); // settle del scroll antes de que el Blackout mida
            },
            wasCollapsed ? 400 : 0,
        );
    });
};

/**
 * Mapa `target_selector` → hook de layout móvil. El backend envía JSON puro (sin funciones), así que el
 * orquestador inyecta estas funciones al adaptar los pasos, según el elemento al que apunta cada paso.
 *
 * ⚠️ Las claves deben coincidir EXACTAMENTE con los `target_selector` del blueprint
 * (`api/src/scripts/onboarding_v1.blueprint.ts`). Si allí cambia un selector, actualizar acá.
 */
const MOBILE_HOOKS_BY_SELECTOR: Record<string, () => void | Promise<void>> = {
    '[data-tour="btn-nueva-cuenta"]': closeSidebarIfOpenMobile, // Cuentas · paso 2
    '#tour-product-card-0': closeSidebarIfOpenMobile, // Pedidos · paso 2
    '#tour-client-selector': openCartPanelIfClosedMobile, // Pedidos · paso 3
    '#tour-submit-sale-btn': openCartPanelIfClosedMobile, // Pedidos · paso 4
    '#tour-help-btn': closeCartPanelIfOpenMobile, // Pedidos · paso 5
    '[data-tour="btn-ingreso-inventario"]': closeSidebarIfOpenMobile, // Inventario · paso 2 (cierra el menú hamburguesa; queda cerrado para los pasos 3-5)
    '[data-tour="sidebar-cargos-extra"]': makeRevealSidebarItem('[data-tour="sidebar-cargos-extra"]'), // Cargos extra (F2) · paso 1 (expande "Configuración" si está colapsado + scroll a la vista)
    '[data-tour="sidebar-usuarios-asociados"]': makeRevealSidebarItem('[data-tour="sidebar-usuarios-asociados"]'), // Usuarios asociados (F2) · paso 1
    '[data-tour="sidebar-recetas-combos"]': makeRevealSidebarItem('[data-tour="sidebar-recetas-combos"]'), // Combos y promociones (F2) · paso 1 (acordeón "Inventario")
    '[data-tour="sidebar-precios-venta"]': makeRevealSidebarItem('[data-tour="sidebar-precios-venta"]'), // Canales / Listas de precios (F2) · paso 1 (acordeón "Pedidos")
    '[data-tour="sidebar-cierre-caja"]': makeRevealSidebarItem('[data-tour="sidebar-cierre-caja"]'), // Cierre de caja (F2) · paso 1 (acordeón "Configuración")
    '[data-tour="sidebar-gastos"]': makeRevealSidebarItem('[data-tour="sidebar-gastos"]'), // Gastos (F2) · paso 1 (ítem top-level, SIN acordeón: makeRevealSidebarItem es null-safe → solo scroll)
    '[data-tour="sidebar-pedidos"]': makeRevealSidebarItem('[data-tour="sidebar-pedidos"]'), // Devoluciones (F2) · paso 1 (acordeón "Pedidos" → sub-ítem "Registros de pedidos")
    // Pasos 2 (botones "Ayuda") de Fase 2: en móvil cerrar el menú hamburguesa para que el botón del contenido
    // quede visible (el paso 1 lo dejó abierto para señalar el ítem del sidebar).
    '[data-tour="btn-ayuda-preferencias"]': closeSidebarIfOpenMobile, // Configuración (cargos-extra / usuarios / cierre-caja) · paso 2
    '[data-tour="btn-ayuda-canales-venta"]': closeSidebarIfOpenMobile, // Canales / Listas de precios · paso 2
    '[data-tour="btn-ayuda-recetas-combos"]': closeSidebarIfOpenMobile, // Combos y promociones · paso 2
    '[data-tour="btn-ayuda-gastos"]': closeSidebarIfOpenMobile, // Gastos · paso 2
    '[data-tour="btn-devolucion"]': closeSidebarIfOpenMobile, // Devoluciones · paso 2 (botón devolver en el contenido)
    '[data-tour="btn-ayuda-pedidos"]': closeSidebarIfOpenMobile, // Devoluciones · paso 3 (botón Ayuda de la cabecera)
    '[data-tour="celda-precio"]': closeSidebarIfOpenMobile, // Precios de venta · paso 2 (celda de precio en la grilla; cierra el drawer para pasos 2-4)
};

// ─── Servicio ─────────────────────────────────────────────────────────────────

@Injectable({
    providedIn: 'root',
})
export class TourOrchestratorService implements OnDestroy {
    private readonly _tourState$ = new BehaviorSubject<TourState>(INITIAL_STATE);
    public readonly tourState$: Observable<TourState> = this._tourState$.asObservable();

    /**
     * Suscripción del lanzamiento ENCOLADO del tour: espera (vía `modalOpen$`) a que no haya un modal
     * instructivo abierto antes de arrancar el Blackout. Se cancela en cambio de módulo y en `endTour`.
     */
    private _pendingSub: Subscription | null = null;

    /** Módulo raíz esperado del lanzamiento encolado (para cancelarlo si el usuario cambia de módulo). */
    private _pendingLaunchRoot: string | null = null;

    /**
     * Suscripción del "hand-off" diferido entre módulos (Fase EE.2): retrasa el arranque
     * `TOUR_HANDOFF_DEFER_MS` para que el DOM del modal saliente termine su fade antes de armar el gate. Se
     * cancela si llega otra navegación dentro de esa ventana.
     */
    private _handoffSub: Subscription | null = null;

    /**
     * Auto-trigger pendiente pedido desde la UI (ej. clic en una TaskCard del Home): al llegar a la ruta
     * `root` esperada, se arranca el tour de `taskId`. Evita ensuciar la URL con query-params y no se
     * re-dispara en un F5.
     */
    private _pending: { taskId: string; link: string } | null = null;

    /**
     * Intención de arranque desde el sidebar DIFERIDA (FINA-3295 B): el clic llegó antes de que cargara el
     * estado del onboarding, así que la resolución espera a `settled$`. Un clic posterior la reemplaza.
     */
    private _deferredNavLinkSub: Subscription | null = null;

    /** Origen del próximo blackout (para el prop `trigger` de `onboarding_blackout_shown`). Se resetea a 'auto'. */
    private _lastTrigger: BlackoutTrigger = 'auto';

    /** Segmento raíz de la ruta activa cuando se inició el tour (kill-switch tolerante). */
    private _activeTourRouteRoot: string | null = null;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly router: Router,
        private readonly onboarding: OnboardingService,
        private readonly instructionsModal: InstructionsModalService,
        private readonly analytics: OnboardingAnalyticsService,
    ) {
        this._listenToRouting();
        // Fase LL: registrar el predicado de supresión del auto-show del video. `InstructionsModalService`
        // (app-common) lo consulta en `showModal` sin importar onboarding (Cero Cables). Suprime el video de
        // bienvenida cuando el módulo actual tiene una tarea PENDIENTE con auto_start (Retail).
        //
        // FINA-2826/2892 (fix race de arranque en frío): ambos predicados leen `currentState`, que en un F5 / URL
        // directa es `null` porque el HTTP del estado aún no volvió → antes evaluaban `false` y el video se abría
        // tapando el Blackout. Ahora se envuelven en `_deferUntilOnboardingSettled`, que ESPERA a que la carga del
        // onboarding resuelva (`settled$`) antes de evaluar. El hook devuelve `Observable<boolean>`; el modal lo
        // resuelve async y solo abre el video si el resultado final es `false`.
        this.instructionsModal.registerVideoSuppressionCheck(() => this._deferUntilOnboardingSettled(() => this._isEntryVideoSuppressed()));
        // Auto-show del video suprimido cuando la tarea de onboarding de ese video YA está completada (y el
        // servicio marca "no volver a mostrar"). Predicado keyed por `instructionsSetName`.
        this.instructionsModal.registerCompletedVideoCheck(videoKey => this._deferUntilOnboardingSettled(() => this._isCompletedTaskVideo(videoKey)));

        // Persistir "no volver a mostrar" PROACTIVAMENTE al completarse la tarea (no lazy en el próximo
        // showModal). El auto-video del módulo corre en `ngOnInit`, y en un F5 (o al volver de una sub-ruta como
        // Devoluciones) puede dispararse ANTES de que el estado de onboarding cargue → la comprobación lazy
        // fallaría y el video reaparecería en cada recarga. Al marcar el flag en el instante en que la tarea
        // cruza a completada (estado ya cargado), el guard estándar `shouldBlock` (user.instructionsNotToShow)
        // bloquea el auto-video en cualquier carga posterior — sin depender del timing del estado en showModal.
        this.onboarding.taskCompleted$.pipe(takeUntil(this._destroy$)).subscribe(({ task }) => {
            const videoKey = TASK_VIDEO_KEY_MAP[task.task_id];
            if (videoKey) this.instructionsModal.markDoNotShowAgainSilently(videoKey);
        });
    }

    // ─── Getters ──────────────────────────────────────────────────────────────

    public get currentState(): TourState {
        return this._tourState$.getValue();
    }

    public get currentStep(): TourStep | null {
        const state = this.currentState;
        if (!state.isActive || !state.currentTour) return null;
        return state.currentTour.steps[state.currentStepIndex] ?? null;
    }

    public get totalSteps(): number {
        return this.currentState.currentTour?.steps.length ?? 0;
    }

    // ─── Ciclo de vida ────────────────────────────────────────────────────────

    ngOnDestroy(): void {
        this._cancelPendingTrigger();
        this._deferredNavLinkSub?.unsubscribe();
        this._deferredNavLinkSub = null;
        this._destroy$.next();
        this._destroy$.complete();
    }

    // ─── Resolución data-driven (desde OnboardingService.state$) ───────────────

    /** Busca una tarea por `task_id` en todas las fases del estado actual. */
    private findTaskInState(taskId: string): OnboardingTask | null {
        const state = this.onboarding.currentState;
        if (!state) return null;
        for (const phase of state.phases) {
            const task = phase.tasks.find(t => t.task_id === taskId);
            if (task) return task;
        }
        return null;
    }

    /**
     * True si la tarea existe en el estado actual y NO está completada (hay onboarding activo y esa tarea
     * sigue pendiente). Consumido por el sidebar para decidir si un ítem con `tourId` desvía al tour o se
     * comporta normal (acordeón/navegación): sin onboarding o tarea completada ⇒ `false`.
     */
    public isTaskPending(taskId: string): boolean {
        const task = this.findTaskInState(taskId);
        return !!task && !task.isCompleted;
    }

    /**
     * Condición COMPLETA para que un ítem del sidebar desvíe el clic al tour: tarea pendiente Y de la fase
     * ACTIVA. Antes `toggle()` decidía solo con `isTaskPending` y la barrera de fase (dentro de
     * `startTourByTaskId`) recién rechazaba el tour DESPUÉS de navegar: con una tarea pendiente de OTRA fase
     * (estado sintético, o una tarea nueva agregada por definición a una fase ya superada), el clic en el
     * acordeón "Pedidos" navegaba a "Nuevo pedido" —creando un pedido— y el tour igual se omitía. Validar la
     * fase ANTES de navegar deja el acordeón/link en su comportamiento normal en esos casos. Sin estado
     * (arranque en frío) ⇒ `false`, igual que `isTaskPending` (la intención diferida de FINA-3295 B cubre
     * ese camino vía `requestTourForNavLink`).
     */
    public isTaskPendingInActivePhase(taskId: string): boolean {
        return this.isTaskPending(taskId) && this._isTaskInActivePhase(taskId);
    }

    /**
     * Núcleo del fix de race (FINA-2826/2892). Difiere la evaluación de un predicado que depende de
     * `currentState` hasta que la carga inicial del onboarding RESOLVIÓ (`onboarding.settled$` → true), evitando
     * el falso `false` del arranque en frío (F5 / URL directa) donde `currentState` es `null` sólo porque el HTTP
     * aún no volvió. Devuelve un `Observable<boolean>` de UNA sola emisión, sin leaks:
     *  - `filter(Boolean) + take(1)`: espera el primer `settled=true` (o emite YA si la carga estaba resuelta —
     *    navegación en caliente) y completa; nada queda suscrito.
     *  - `map(evaluate)`: recién ahí evalúa el predicado síncrono contra el estado ya poblado.
     *  - `timeout(...→ of(false))`: si la carga nunca resolviera (caso patológico, `loadInitialState` no
     *    invocado), a los N ms resuelve `false` (NO suprimir) para no bloquear el video (CN-03).
     *  - `catchError(→ of(false))`: cualquier error del stream ⇒ `false` (no suprimir), fallback seguro.
     * `settled$` cubre CN-01 (404 "no aplica" ⇒ settled ⇒ predicado evalúa sin estado ⇒ false ⇒ el video abre)
     * y CN-03 (5xx ⇒ settled ⇒ false), sin esperar al timeout en esos casos.
     */
    private _deferUntilOnboardingSettled(evaluate: () => boolean): Observable<boolean> {
        return this.onboarding.settled$.pipe(
            filter(settled => settled === true),
            take(1),
            map(() => evaluate()),
            timeout({ first: ENTRY_VIDEO_DECISION_TIMEOUT_MS, with: () => of(false) }),
            catchError(() => of(false)),
        );
    }

    /**
     * Predicado de supresión del auto-show del video (Fase LL, registrado en `InstructionsModalService`). True
     * cuando la RUTA ACTUAL del navegador tiene una tarea de onboarding PENDIENTE con `auto_start` → el blackout
     * guiará al usuario (incluido el paso "Consulta un video"), así que el video de bienvenida NO debe abrirse.
     * Sin estado: reusa `_findAutoStartTaskByRoute` (que ya filtra completadas y sin onboarding) → tarea
     * completada / no-Retail ⇒ false ⇒ el video se muestra normal. Cubre entrada por sidebar/TaskCard, el
     * hand-off (POS→Inventario) y el F5, porque se evalúa en el instante del `showModal`.
     */
    private _isEntryVideoSuppressed(): boolean {
        return this._findAutoStartTaskByRoute(this._extractRouteRoot(this.router.url)) !== null;
    }

    /**
     * ¿El video `videoKey` (`instructionsSetName`) corresponde a una tarea de onboarding YA COMPLETADA? Se usa
     * para que `InstructionsModalService` NO auto-muestre el video (y marque "no volver a mostrar") cuando la
     * tarea ya se cumplió. Sin mapa para esa key, sin estado, o tarea inexistente/pendiente ⇒ false ⇒ el video
     * se comporta como hoy. Phase-agnóstico: si la tarea está completada, se suprime sin importar la fase actual.
     */
    private _isCompletedTaskVideo(videoKey: string): boolean {
        const taskId = VIDEO_KEY_TO_TASK[videoKey];
        if (!taskId) return false;
        return this.findTaskInState(taskId)?.isCompleted === true;
    }

    /**
     * Primera tarea INCOMPLETA cuyo módulo (raíz de su `link`) sea `root` y cuyo tour tenga `auto_start`.
     * Soporta el "hand-off" tras un redirect entre módulos (ej. POS→Inventario por inventario vacío): al
     * aterrizar en la ruta destino se arranca su tour. No dispara nada por sí mismo — el guard `isCompleted`,
     * el gate del modal y la protección de pasos vacíos los aplica `startTourByTaskId`.
     */
    private _findAutoStartTaskByRoute(root: string): OnboardingTask | null {
        if (!root) return null;
        const state = this.onboarding.currentState;
        if (!state) return null;
        for (const phase of state.phases) {
            for (const task of phase.tasks) {
                if (task.isCompleted || !task.guided_tour?.auto_start) continue;
                if (this._extractRouteRoot(task.link || '') === root) return task;
            }
        }
        return null;
    }

    /**
     * Adapta el `guided_tour` del backend (snake_case) al contrato `GuidedTour`/`TourStep` del blackout,
     * inyectando los hooks móviles por `target_selector`. Devuelve null si la tarea no tiene pasos.
     */
    private buildTourFromTask(task: OnboardingTask): GuidedTour | null {
        const rawSteps = task.guided_tour?.steps ?? [];
        // Ignora pasos sin selector configurado (ej. inventario aún sin anclajes). Evita que llegue un
        // `target_selector` vacío al blackout (rompería document.querySelector('')). Si tras filtrar no queda
        // ningún paso, la tarea NO dispara tour → navegación normal.
        const validSteps = rawSteps.filter(s => !!s.target_selector && s.target_selector.trim() !== '');
        if (!validSteps.length) return null;

        return {
            tourId: task.task_id, // el task_id pasa a ser el identificador del tour (persistencia/kill-switch)
            tourName: task.title,
            steps: validSteps.map<TourStep>(s => ({
                targetElement: s.target_selector, // ADAPTADOR: target_selector (backend) → targetElement (TourStep)
                title: s.title,
                content: s.content,
                placement: (s.placement || 'bottom') as TourStepPlacement,
                beforeShowAction: MOBILE_HOOKS_BY_SELECTOR[s.target_selector], // undefined si el paso no tiene hook
            })),
        };
    }

    // ─── Listener de enrutamiento ──────────────────────────────────────────────

    private _listenToRouting(): void {
        let isAppBooting = true;

        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this._destroy$),
            )
            .subscribe((event: NavigationEnd) => {
                // Cold Boot (F5 / re-login): restaurar un tour interrumpido cuando el state$ esté disponible.
                if (isAppBooting) {
                    isAppBooting = false;
                    this._restorePersistedStateWhenReady();
                    return;
                }

                const url = event.urlAfterRedirects;
                const incomingRoot = this._extractRouteRoot(url);

                // Una nueva navegación invalida cualquier hand-off diferido aún pendiente (Fase EE.2).
                this._handoffSub?.unsubscribe();
                this._handoffSub = null;

                // Kill-switch por PÁGINA: con un tour activo, cierra si el usuario SALE de la página de la
                // tarea (`task.link`). Antes toleraba todo el módulo raíz, pero eso dejaba vivo un tour cuyos
                // anclajes son globales (sidebar + header compartido) al cambiar de tab dentro del mismo módulo
                // (ej. entre tabs de /comercio/*). Se compara por link exacto o sub-ruta; si la tarea no trae
                // link, cae al comportamiento anterior (por raíz). Los tours actuales son de una sola página.
                if (this.currentState.isActive) {
                    const activeTour = this.currentState.currentTour;
                    const activeTask = activeTour ? this.findTaskInState(activeTour.tourId) : null;
                    const taskLink = (activeTask?.link || '').split('?')[0].split('#')[0];
                    const cleanUrl = url.split('?')[0].split('#')[0];
                    const stillOnTourPage = taskLink ? cleanUrl === taskLink || cleanUrl.startsWith(taskLink + '/') : incomingRoot === this._activeTourRouteRoot;
                    if (!stillOnTourPage) {
                        this.endTour();
                    }
                    return;
                }

                // Si hay un lanzamiento ENCOLADO (esperando que cierre un modal) y el usuario cambió de
                // módulo antes de que arrancara, se cancela (ya no aplica a esta ruta).
                let killedPending = false;
                if (this._pendingSub && this._pendingLaunchRoot !== null && incomingRoot !== this._pendingLaunchRoot) {
                    this._cancelPendingTrigger();
                    killedPending = true;
                }

                // Auto-trigger pendiente: se consume en la PRIMERA NavigationEnd tras la petición.
                // `startTourByTaskId` aplica internamente el gate del modal + el buffer de render.
                if (this._pending) {
                    const pending = this._pending;
                    this._pending = null;
                    // Consumir por PÁGINA (link exacto o sub-ruta), NO por raíz: así navegar a otra tab del
                    // mismo módulo (ej. /comercio/discounts con un pending de /comercio/staff) ya no arranca el
                    // tour en la página equivocada.
                    const cleanUrl = url.split('?')[0].split('#')[0];
                    if (cleanUrl === pending.link || cleanUrl.startsWith(pending.link + '/')) {
                        this.startTourByTaskId(pending.taskId);
                        return; // pending explícito ya arrancó/encoló → no hacer hand-off
                    }
                }

                // Hand-off (Cero Cables): si este cambio de módulo canceló un tour encolado y la nueva ruta es
                // el destino de otra tarea incompleta con auto_start, arrancamos su tour. Cubre POS→Inventario
                // (inventario vacío): el kill-switch mató "primera venta" y aquí engancha "carga tu primer
                // producto". Acotado a `killedPending` → no secuestra navegaciones normales a Inventario.
                if (killedPending && !this.currentState.isActive && !this._pendingSub) {
                    const handoff = this._findAutoStartTaskByRoute(incomingRoot);
                    if (handoff) {
                        // Diferir el arranque (Fase EE.2): al pulsar "Ir a inventario" el modal saliente del POS
                        // aún está en su fade (backdrop en el DOM). Si armáramos el gate ahora, `_isUiBlocked()`
                        // vería ese modal viejo y el race se engancharía a SU desaparición, arrancando antes de
                        // que abra el modal de bienvenida de Inventario. Esperamos a que el DOM anterior muera.
                        this._handoffSub = timer(TOUR_HANDOFF_DEFER_MS)
                            .pipe(take(1), takeUntil(this._destroy$))
                            .subscribe(() => {
                                this._handoffSub = null;
                                // Revalidar: una navegación posterior invalida el hand-off; solo arrancar si
                                // seguimos en la ruta destino y sin tour activo/encolado.
                                if (this.currentState.isActive || this._pendingSub) return;
                                if (this._extractRouteRoot(this.router.url) !== incomingRoot) return;
                                this.startTourByTaskId(handoff.task_id);
                            });
                    }
                }
            });
    }

    /** Primer segmento significativo de una URL (sin query/fragment). '/pos/direct-sales?x=1' → 'pos'. */
    private _extractRouteRoot(url: string): string {
        const cleanPath = url.split('?')[0].split('#')[0];
        const segments = cleanPath.split('/').filter(Boolean);
        return segments[0] ?? '';
    }

    /** True si la UI está visualmente bloqueada por un modal (señal del servicio de instrucciones + DOM). */
    private _isUiBlocked(): boolean {
        return this.instructionsModal.isModalOpen || document.querySelector(BLOCKING_MODAL_SELECTOR) !== null;
    }

    private _cancelPendingTrigger(): void {
        this._pendingSub?.unsubscribe();
        this._pendingSub = null;
        this._pendingLaunchRoot = null;
    }

    // ─── Persistencia (sessionStorage) ─────────────────────────────────────────

    private _persistState(taskId: string, stepIndex: number): void {
        try {
            sessionStorage.setItem(TOUR_STORAGE_KEY, taskId);
            sessionStorage.setItem(TOUR_STEP_KEY, String(stepIndex));
        } catch {
            // Storage no disponible: el tour sigue en memoria, sin persistencia.
        }
    }

    private _clearPersistedState(): void {
        try {
            sessionStorage.removeItem(TOUR_STORAGE_KEY);
            sessionStorage.removeItem(TOUR_STEP_KEY);
        } catch {
            // Ignorar.
        }
    }

    /**
     * Restaura un tour interrumpido tras un Cold Boot. Como `state$` llega async (HTTP tras login), espera
     * el primer estado no-nulo, reconstruye el tour por `task_id`, valida el `stepIndex` y —vía el guard de
     * restauración— que la ruta actual del navegador pertenezca al módulo de la tarea antes de activar.
     */
    private _restorePersistedStateWhenReady(): void {
        let taskId: string | null;
        let stepRaw: string | null;
        try {
            taskId = sessionStorage.getItem(TOUR_STORAGE_KEY);
            stepRaw = sessionStorage.getItem(TOUR_STEP_KEY);
        } catch {
            return;
        }
        if (!taskId || stepRaw === null) return;

        const stepIndex = parseInt(stepRaw, 10);
        if (isNaN(stepIndex) || stepIndex < 0) {
            this._clearPersistedState();
            return;
        }

        this.onboarding.state$
            .pipe(
                filter((s): s is OnboardingState => !!s),
                take(1),
                takeUntil(this._destroy$),
            )
            .subscribe(() => {
                const task = this.findTaskInState(taskId as string);
                if (!task || task.isCompleted) {
                    this._clearPersistedState();
                    return;
                }
                // GUARD DE RESTAURACIÓN — Cold Boot en ruta distinta: el kill-switch solo escucha
                // NavigationEnd (SPA), así que es CIEGO a un F5 directo sobre otra ruta. Validamos que la ruta
                // ACTUAL del navegador pertenezca al módulo destino de la tarea; si no, NO restauramos (taparía
                // la pantalla equivocada) y limpiamos el estado persistido.
                const taskRoot = this._extractRouteRoot(task.link || '');
                const currentRoot = this._extractRouteRoot(this.router.url);
                if (!taskRoot || taskRoot !== currentRoot) {
                    this._clearPersistedState();
                    return;
                }
                const tour = this.buildTourFromTask(task);
                if (!tour || stepIndex >= tour.steps.length) {
                    this._clearPersistedState();
                    return;
                }
                this._activeTourRouteRoot = taskRoot;
                this.hideIntercom(); // el Blackout se restaura visible (F5 a mitad de tour) → ocultar Intercom
                this._tourState$.next({ isActive: true, currentTour: tour, currentStepIndex: stepIndex });
            });
    }

    // ─── Acciones públicas ─────────────────────────────────────────────────────

    /**
     * Barrera de fase: ¿la tarea pertenece a la fase ACTIVA del comercio? Fuente de verdad = `getActivePhase`
     * (currentPhaseId/status del backend). Se usa para NO arrancar tours de tareas de otra fase cuando el
     * usuario navega libremente a su vista (ej. está en Fase 1 y entra a una vista de Fase 2). Sin estado de
     * onboarding → false (no hay fase que respetar; el arranque igual saldría en silencio más adelante).
     * Onboarding cerrado (`completion.completed`, FINA-3377) → false: ya no hay fase activa, así que tampoco
     * arrancan los tours de opcionales que quedaron pendientes al cerrar por tiempo.
     */
    private _isTaskInActivePhase(taskId: string): boolean {
        const state = this.onboarding.currentState;
        if (!state || state.completion?.completed) return false;
        const activePhase = this.onboarding.getActivePhase(state);
        return !!activePhase?.tasks?.some(t => t.task_id === taskId);
    }

    /**
     * Inicia un tour resolviéndolo desde el estado global por `task_id`.
     * Punto de entrada para disparos manuales (clic en el ítem del sidebar con `tourId = task_id`).
     */
    public startTourByTaskId(taskId: string): void {
        if (this.currentState.isActive) return;

        const task = this.findTaskInState(taskId);
        if (!task) {
            // Sin estado de onboarding (p. ej. tenant no-Retail: /status → 404 → state$ = null): salir en
            // silencio. El warning solo aplica si SÍ hay estado activo pero falta esa tarea puntual.
            if (this.onboarding.currentState) {
                console.warn('[TourOrchestrator] No se encontró la tarea "' + taskId + '" en el estado actual.');
            }
            return;
        }
        // Regla de negocio: no se muestra el tour de una tarea ya completada. Guard central → protege
        // TODOS los orígenes (sidebar y auto-trigger de la TaskCard pasan por aquí).
        if (task.isCompleted) {
            console.info("[TourOrchestrator] Tour omitido: la tarea '" + taskId + "' ya está completada.");
            return;
        }
        // BARRERA DE FASE (Phase Guard): el Blackout solo arranca si la tarea pertenece a la fase ACTIVA. Si el
        // usuario está en Fase 1 y navega libremente a una vista de Fase 2, su tour se ignora en silencio
        // (coherente con el gate de fase del backend, que tampoco completaría esa tarea fuera de fase). Guard
        // central → protege TODOS los orígenes (sidebar, _pending de la TaskCard, hand-off entre módulos).
        if (!this._isTaskInActivePhase(taskId)) {
            console.info("[TourOrchestrator] Tour omitido: la tarea '" + taskId + "' no pertenece a la fase activa.");
            return;
        }
        const tour = this.buildTourFromTask(task);
        if (!tour) {
            console.warn('[TourOrchestrator] La tarea "' + taskId + '" no tiene pasos de tour.');
            return;
        }

        // ADUANA UNIVERSAL (gate de UNA fase, DOM-agnóstico — Cero Cables): con el video del módulo YA diferido
        // (Fase LL) no hay que esperar a que APAREZCA un modal. Solo evitamos pintar el Blackout encima de un
        // modal INCIDENTAL que YA esté abierto (plan-permission, SweetAlert, otro ngb): si la UI está libre →
        // arrancar YA; si hay modal → esperar a que se despeje + un settle corto. El `LAYOUT_SETTLE_MS` del
        // blackout sigue vigente para posicionar bien el paso 1.
        this._cancelPendingTrigger();

        if (!this._isUiBlocked()) {
            if (!this.currentState.isActive) this.startTour(tour);
            return;
        }

        this._pendingLaunchRoot = this._extractRouteRoot(this.router.url);
        this._pendingSub = timer(0, TOUR_GATE_POLL_MS)
            .pipe(
                map(() => this._isUiBlocked()),
                filter(blocked => !blocked), // esperar a que la pantalla quede DESPEJADA
                take(1),
                delay(TOUR_GATE_CLEAR_SETTLE_MS), // + settle corto (no pintar en mitad de la animación)
                takeUntil(this._destroy$),
            )
            .subscribe(() => {
                this._pendingSub = null;
                this._pendingLaunchRoot = null;
                if (!this.currentState.isActive) this.startTour(tour);
            });
    }

    /**
     * Registra un auto-trigger de tour para consumirlo tras navegar a `link` (ej. clic en una TaskCard).
     * No-op efectivo si la tarea no tiene pasos (se valida al arrancar en `startTourByTaskId`).
     */
    public requestTourForTask(taskId: string, link: string): void {
        const cleanLink = link.split('?')[0].split('#')[0];
        this._pending = { taskId, link: cleanLink };
        this._lastTrigger = 'banner_click'; // origen del blackout: click en TaskCard/checklist del Home
        // Si YA estamos en la página destino, la navegación del caller (navigateByUrl al MISMO url) es no-op y
        // NO dispara NavigationEnd → el `_pending` nunca se consumiría (el tour no arrancaría y, peor, el
        // pending viejo se dispararía en la próxima navegación a otra tab del mismo módulo). Arrancar YA.
        const cleanCurrent = this.router.url.split('?')[0].split('#')[0];
        if (cleanCurrent === cleanLink || cleanCurrent.startsWith(cleanLink + '/')) {
            const pending = this._pending;
            this._pending = null;
            this.startTourByTaskId(pending.taskId);
        }
    }

    /**
     * Para el clic MANUAL en un ítem del sidebar (Cero Cables): si `link` es el destino de una tarea de
     * onboarding PENDIENTE con `auto_start`, encola su tour para que arranque al aterrizar (flujo `_pending`
     * → NavigationEnd → gate). Match por link EXACTO o cuando el link de la tarea es una SUB-ruta del ítem
     * clickeado (ej. ítem "Gastos" → `/expenses`, cuya tarea navega a `/expenses/variable-expenses` por el
     * redirect del módulo). El `+ '/'` exige sub-ruta propia (no secuestra otras tabs del mismo módulo: el
     * kill-switch se ancla al link REAL de la tarea). Encolamos con `task.link` para que el `_pending` case por
     * página funcione tras el redirect. No-op si no aplica (no-Retail / sin tarea / completada).
     * El sidebar solo pasa el `link`; la resolución de la tarea vive aquí.
     */
    public requestTourForNavLink(link: string): boolean {
        if (!link) return false;
        // FINA-3295 (B): con la API lenta el clic del sidebar puede llegar ANTES de que el estado del
        // onboarding cargue (`currentState` aún null). Antes se devolvía false y la intención se perdía para
        // siempre: el tour no arrancaba ni siquiera cuando el estado llegaba después (QA: 12 de 12 con +6 s).
        // Ahora la intención se recuerda y se re-resuelve al emitir `settled$` (ver _rememberNavLinkIntent).
        if (!this.onboarding.currentState) {
            this._rememberNavLinkIntent(link);
            return false;
        }
        return this._resolveNavLinkTour(link);
    }

    /** Resolución síncrona del tour por link (requiere estado cargado). Separada de `requestTourForNavLink`
     *  para que la re-resolución diferida (FINA-3295 B) no pueda re-diferirse en bucle cuando `settled$`
     *  resuelve SIN estado (404 no-Retail / 5xx): aquí eso simplemente devuelve false. */
    private _resolveNavLinkTour(link: string): boolean {
        const state = this.onboarding.currentState;
        if (!state) return false;
        for (const phase of state.phases) {
            for (const task of phase.tasks) {
                if (task.isCompleted || !task.guided_tour?.auto_start) continue;
                const taskLink = task.link;
                if (!taskLink) continue;
                if (taskLink === link || taskLink.startsWith(link + '/')) {
                    // Barrera de fase: no encolar el tour si la tarea es de otra fase (navegación libre a una
                    // vista de fase futura). Seguimos buscando por si otra tarea de la fase activa matchea el link.
                    if (!this._isTaskInActivePhase(task.task_id)) continue;
                    this.requestTourForTask(task.task_id, taskLink);
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * FINA-3295 (CB-14): intención diferida para el clic temprano en un acordeón PADRE del sidebar (ej.
     * "Inventario", con `tourId` y sin link). Con el estado del onboarding aún sin cargar, `toggle()` no puede
     * saber si la tarea aplica; aquí se recuerda y, al emitir `settled$`:
     *  - si la tarea sigue PENDIENTE y es de la FASE ACTIVA, y
     *  - el usuario SIGUE en la misma página donde hizo el clic (no interrumpir otra vista),
     * se navega al primer hijo del acordeón y se encola su tour (mismo flujo `_pending` → NavigationEnd →
     * gate de modal que una TaskCard). Con estado ya cargado es un no-op: la rama del toggle decidió de verdad.
     * Comparte la suscripción con la intención por link (un clic posterior reemplaza al anterior).
     */
    public rememberParentTourIntent(taskId: string, firstChildLink: string): void {
        if (!taskId || !firstChildLink || this.onboarding.currentState) return;
        const originUrl = this.router.url.split('?')[0].split('#')[0];
        const cleanChildLink = firstChildLink.split('?')[0].split('#')[0];
        this._deferredNavLinkSub?.unsubscribe();
        this._deferredNavLinkSub = this.onboarding.settled$
            .pipe(
                filter(settled => settled === true),
                take(1),
                map(() => true),
                timeout({ first: NAV_LINK_INTENT_TIMEOUT_MS, with: () => of(false) }),
                catchError(() => of(false)),
                takeUntil(this._destroy$),
            )
            .subscribe(settledOk => {
                this._deferredNavLinkSub = null;
                if (!settledOk) return; // tope agotado: la carga nunca resolvió → descartar la intención
                if (!this.isTaskPendingInActivePhase(taskId)) return; // completada o de otra fase → acordeón normal
                const current = this.router.url.split('?')[0].split('#')[0];
                if (current !== originUrl) return; // el usuario ya navegó a otra parte
                this.requestTourForTask(taskId, cleanChildLink);
                this.router.navigateByUrl(cleanChildLink);
            });
    }

    /**
     * FINA-3295 (B): recuerda la intención de arrancar un tour desde el sidebar cuando el clic llegó antes de
     * que cargara el estado del onboarding, y la resuelve cuando `settled$` emite (el estado llegó — o la
     * carga terminó en 404/5xx, en cuyo caso `_resolveNavLinkTour` devuelve false y no pasa nada).
     *
     * Guardas:
     *  - Tope propio (`NAV_LINK_INTENT_TIMEOUT_MS`): si la carga nunca resuelve, la intención se descarta.
     *  - Guard de URL al resolver: solo arranca si el usuario SIGUE en la página del ítem clickeado (link
     *    exacto o sub-ruta, mismas semánticas del match del `_pending`); si navegó a otra parte, se descarta.
     *  - Un clic posterior en otro ítem reemplaza la intención anterior (unsubscribe).
     *  - El arranque pasa por `startTourByTaskId` → gate de modal incluido: si el modal de novedades llega
     *    junto con el estado (QA lo observó a los 8–11 s), el tour espera a que se cierre y arranca después.
     */
    private _rememberNavLinkIntent(link: string): void {
        this._deferredNavLinkSub?.unsubscribe();
        this._deferredNavLinkSub = this.onboarding.settled$
            .pipe(
                filter(settled => settled === true),
                take(1),
                map(() => true),
                timeout({ first: NAV_LINK_INTENT_TIMEOUT_MS, with: () => of(false) }),
                catchError(() => of(false)),
                takeUntil(this._destroy$),
            )
            .subscribe(settledOk => {
                this._deferredNavLinkSub = null;
                if (!settledOk) return; // tope agotado: la carga nunca resolvió → descartar la intención
                const cleanCurrent = this.router.url.split('?')[0].split('#')[0];
                if (cleanCurrent !== link && !cleanCurrent.startsWith(link + '/')) return; // el usuario se fue
                this._resolveNavLinkTour(link);
            });
    }

    /**
     * Oculta el widget de Intercom (chat de soporte) para que no se superponga al Blackout del tour. Safe call:
     * si un adblocker bloqueó el script o el SDK aún no booteó, `window.Intercom` no es función → no-op (nunca
     * rompe el tour). "Cero Cables": vive en el orquestador; los módulos de negocio no se enteran.
     */
    private hideIntercom(): void {
        try {
            const intercom = typeof window !== 'undefined' ? (window as any).Intercom : undefined;
            if (typeof intercom !== 'function') return;
            // Ocultar la BURBUJA del launcher (no solo cerrar el panel): así no se superpone al Blackout. El boot
            // no setea `hide_default_launcher` (default `false` = burbuja visible), por lo que `true` la esconde.
            intercom('update', { hide_default_launcher: true });
            // Además cerrar el panel si un mensaje proactivo lo abrió sobre el Blackout (`hide_default_launcher`
            // solo controla la burbuja, no cierra un Messenger ya abierto).
            intercom('hide');
        } catch {
            // Intercom no disponible / falló: ignorar, el tour continúa.
        }
    }

    /**
     * Restaura el widget de Intercom al terminar el tour: revierte EXACTAMENTE lo que hizo `hideIntercom` (vuelve a
     * mostrar la burbuja del launcher). NO usa `Intercom('show')`: ese comando DESPLIEGA el panel de chat completo
     * y hacía que el chat se abriera solo al pulsar "Finalizar" (regresión reportada por QA). Solo reponemos la
     * visibilidad de la burbuja con `hide_default_launcher: false`; el usuario abre el chat si lo desea. Mismo safe
     * call: sin Intercom (adblocker / no booteó) es un no-op.
     */
    private showIntercom(): void {
        try {
            const intercom = typeof window !== 'undefined' ? (window as any).Intercom : undefined;
            if (typeof intercom !== 'function') return;
            intercom('update', { hide_default_launcher: false });
        } catch {
            // Intercom no disponible / falló: ignorar.
        }
    }

    /** Inicia un tour ya construido. Registra el route root para el kill-switch. */
    public startTour(tour: GuidedTour): void {
        if (!tour.steps.length) {
            console.warn('[TourOrchestrator] El tour "' + tour.tourName + '" no tiene pasos definidos.');
            return;
        }
        this.hideIntercom(); // que el chat de soporte no tape el Blackout
        this._activeTourRouteRoot = this._extractRouteRoot(this.router.url);
        this._tourState$.next({
            isActive: true,
            currentTour: tour,
            currentStepIndex: 0,
        });
        this._persistState(tour.tourId, 0);
        // onboarding_blackout_shown: el blackout de esta tarea (tour) se muestra. `tourId === task_id`.
        this.analytics.blackoutShown(tour.tourId, this._lastTrigger);
        this._lastTrigger = 'auto';
    }

    /** Avanza al siguiente paso; si es el último, finaliza. */
    public nextStep(): void {
        const state = this.currentState;
        if (!state.isActive || !state.currentTour) return;

        const nextIndex = state.currentStepIndex + 1;
        if (nextIndex >= state.currentTour.steps.length) {
            this.endTour();
        } else {
            this.hideIntercom(); // por seguridad: re-ocultar si Intercom reapareció (mensaje proactivo) entre pasos
            this._tourState$.next({ ...state, currentStepIndex: nextIndex });
            this._persistState(state.currentTour.tourId, nextIndex);
        }
    }

    /** Finaliza y resetea el tour. */
    public endTour(): void {
        this._cancelPendingTrigger(); // por si quedaba un lanzamiento encolado esperando el modal
        this._clearPersistedState();
        this._activeTourRouteRoot = null;
        this._tourState$.next(INITIAL_STATE);
        this.showIntercom(); // restaurar el chat de soporte que `hideIntercom` ocultó durante el Blackout
        // Fase LL (enfoque simple): NO se lanza el video al terminar el tour. Mientras la tarea siga PENDIENTE,
        // el auto-show sigue suprimido por `_isEntryVideoSuppressed`; el video solo aparece cuando la tarea ya
        // esté COMPLETADA (o por clic manual en "Ayuda"). Sin encolar ni re-mostrar nada.
    }
}
